import {
  Box,
  Flex,
  Text,
  Image,
  Badge,
  Divider,
  Link,
} from '@chakra-ui/react';
import { Link as ReachLink, useParams, Navigate } from 'react-router-dom';
import { INTERVIEWS } from '../Components/info_all_interviews';
import { INTERVIEW_ENGLISH } from '../Components/interview_english';
import ReviewWarning from '../Components/ReviewWarning';
import { useLanguage } from '../i18n/LanguageContext';
import { getLocalizedMember } from '../i18n/translations';

const InterviewDetail = () => {
  const { language, t } = useLanguage();
  const { id } = useParams();
  const originalInterview = INTERVIEWS.find(
    (i) => String(i.id) === id && i.published !== false
  );

  if (!originalInterview) return <Navigate to="/interview" replace />;
  const interview = language === 'en' && INTERVIEW_ENGLISH[id]
    ? { ...originalInterview, ...INTERVIEW_ENGLISH[id] }
    : originalInterview;
  const expert = getLocalizedMember(interview.expert, language);

  return (
    <Box bg="#edf3f8" p={{ base: 4, md: 10 }} minH="100vh">
      <Box maxW="5xl" mx="auto">
        {/* Back link */}
        <Link
          as={ReachLink}
          to="/interview"
          fontSize="sm"
          color="#437070"
          _hover={{ color: '#1e646e' }}
          display="inline-flex"
          alignItems="center"
          gap={1}
          mb={6}
        >
          {t('interview.back')}
        </Link>
        {/* Hero card */}
        <Box bg="white" borderRadius="xl" overflow="hidden" boxShadow="sm" border="1px solid" borderColor="gray.100" mb={6}>
          {/* Banner */}
          <Box
            h={{ base: '48', md: '64' }}
            bgGradient="linear(to-br, #1e646e, #002c2f)"
            position="relative"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            p={{ base: 6, md: 8 }}
          >
            <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="widest" mb={2}>
              {t('interview.label')}
            </Text>
            <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color="white" lineHeight="short" maxW={{ base: '2xl', md: '4xl' }}>
              {interview.title}
            </Text>
            <Text fontSize="xs" color="whiteAlpha.700" mt={3}>
              {interview.date}
            </Text>
          </Box>

          {/* Tags */}
          <Flex flexWrap="wrap" gap={2} px={{ base: 6, md: 8 }} py={4} borderBottom="1px solid" borderColor="gray.100">
            {interview.tags.map((tag) => (
              <Badge
                key={tag}
                px={2.5}
                py={0.5}
                borderRadius="full"
                bg="#edf3f8"
                color="#1e4f4f"
                fontWeight="medium"
                fontSize="xs"
              >
                {tag}
              </Badge>
            ))}
          </Flex>
        </Box>

        {/* Layout: article + sidebar */}
        <Flex gap={8} flexDirection={{ base: 'column', lg: 'row' }} alignItems="flex-start">
          {/* Article */}
          <Box flex={1} minW={0}>
            {/* Intro blockquote */}
            <Text
              fontSize="sm"
              color="gray.600"
              lineHeight="tall"
              borderLeft="4px solid"
              borderColor="#437070"
              pl={4}
              fontStyle="italic"
              mb={8}
              textAlign="left"
            >
              {interview.intro}
            </Text>

            {/* Sections */}
            {interview.sections.map((section) => (
              <Box key={section.title} mb={8} id={section.title}>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="#1e4f4f"
                  pb={2}
                  mb={4}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  textAlign="left"
                >
                  {section.title}
                </Text>
                {section.paragraphs.map((para, i) => (
                  <Text key={i} fontSize="sm" color="gray.700" lineHeight="tall" mb={3} textAlign="left">
                    {para}
                  </Text>
                ))}
              </Box>
            ))}
          </Box>

          {/* Sidebar */}
          <Box
            w={{ base: 'full', lg: '72' }}
            flexShrink={0}
            position={{ lg: 'sticky' }}
            top={{ lg: '24' }}
          >
            <Box bg="white" borderRadius="xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
              <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="widest" mb={4}>
                {t('interview.expert')}
              </Text>

              {/* Avatar */}
              {interview.expert.avatar && (
                <Flex justify="center" mb={4}>
                  <Image
                    src={interview.expert.avatar}
                    alt={expert.name_ch}
                    w="20"
                    h="20"
                    borderRadius="full"
                    objectFit="cover"
                    objectPosition="top"
                    ring={2}
                    ringColor="#edf3f8"
                  />
                </Flex>
              )}

              <Box textAlign="center" mb={4}>
                <Text fontWeight="bold" color="#1e4f4f" fontSize="lg">
                  {expert.name_ch}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                  {expert.name_en}
                </Text>
              </Box>

              <Divider mb={4} />

              <Flex flexDirection="column" gap={2} fontSize="sm" color="gray.600" mb={4}>
                <Text textAlign="left">{expert.title}<ReviewWarning message={expert.warning} /></Text>
                {interview.expert.email && (
                  <Link href={`mailto:${interview.expert.email}`} color="#437070" fontSize="xs" _hover={{ textDecoration: 'underline' }} textAlign="left">
                    {interview.expert.email}
                  </Link>
                )}
              </Flex>

              <Divider mb={4} />

              {/* TOC */}
              <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="widest" mb={2} textAlign="left">
                {t('interview.contents')}
              </Text>
              <Flex flexDirection="column" gap={1}>
                {interview.sections.map((section) => (
                  <Link
                    key={section.title}
                    href={`#${section.title}`}
                    fontSize="xs"
                    color="gray.600"
                    _hover={{ color: '#437070' }}
                    lineHeight="short"
                    py={0.5}
                    textAlign="left"
                    display="block"
                  >
                    {section.title}
                  </Link>
                ))}
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default InterviewDetail;
